import React, {useEffect, useMemo, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {collection, doc, getDoc, onSnapshot, updateDoc} from 'firebase/firestore'
import DrawerHeader from 'app/components/Drawer/DrawerHeader'
import { OverlayTrigger, Tooltip} from 'react-bootstrap'
import Button from 'app/components/Button'
import OwnerDisplay from 'app/components/OwnerDisplay'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ReviewConfirmationModal from '../Modals/ReviewConfirmationModal'
import {Link} from 'react-router-dom'
import {ReviewUserSystem, ViewSystemProps} from '../../utils/DrawersUtils'

const ViewSystem: React.FC<ViewSystemProps> = ({
  isOpen,
  onClose,
  id,
  systemId,
  accountId,
  systemName,
  systemStatus,
}) => {
  const {currentUser} = useAuth()
  const [loading, setLoading] = useState(true)
  const [owners, setOwners] = useState<any[]>([])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [members, setMembers] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [reviews, setReviews] = useState<any[]>([])
  const [noteInput, setNoteInput] = useState('')
  const [isNoteSaving, setIsNoteSaving] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<'approve' | 'deny' | null>(null)

  useEffect(() => {
    getReviews()
  }, [id])

  useEffect(() => {
    const filtered = reviews
      .map((review) => ({
        ...review,
        systems: Object.entries(review.systems || {}).map(([systemId, system]: [string, any]) => ({
          systemId,
          ...system,
        })),
      }))
      .flatMap((review) =>
        review.systems.map((system) => ({
          ...review,
          system,
        }))
      )
    setFilteredItems(filtered)
  }, [reviews])

  const selectedSystem = filteredItems.find((item) => item.system.systemId === systemId)

  useEffect(() => {
    if (selectedSystem?.system?.users?.[accountId]?.note) {
      setNoteInput(selectedSystem.system.users[accountId].note)
    } else {
      setNoteInput('')
    }
  }, [selectedSystem, accountId])

  const getReviews = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !id) {
        setIsLoading(false)
        setLoading(false)
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'reviews')

      const unsubscribe = onSnapshot(doc(PolicyRef, id), (snapshot) => {
        const policyData = snapshot.data()
        setReviews(policyData ? [{id: snapshot.id, ...policyData}] : [])
        setIsLoading(false)
        setLoading(false)
      })

      return unsubscribe
    } catch (error) {
      setIsLoading(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!selectedSystem || !currentUser?.tenantId) return

    const tenantId = currentUser.tenantId
    const systemName = selectedSystem.system.name.toLowerCase()
    const accessReviewRef = collection(db, 'tenants', tenantId, 'access_review')

    const unsubscribe = onSnapshot(
      accessReviewRef,
      (snapshot) => {
        const allMembers: any[] = []
        snapshot.docs.forEach((doc) => {
          const assetData = doc.data()
          if (assetData.platform?.toLowerCase() !== systemName) return

          const members = assetData.members || {}
          const org = assetData.org || {}

          Object.keys(members).forEach((memberKey) => {
            const member = members[memberKey]
            allMembers.push({
              id: `${doc.id}-${memberKey}`,
              login: member.login,
              name: member.name,
              mfaEnabled: member.mfaEnabled,
              platform: assetData.platform,
              orgLogin: org.login,
              orgName: org.name,
              status: member.status,
              createdAt: member.createdAt,
              updatedAt: member.updatedAt,
              tenantId: assetData.tenantId,
              uid: assetData.uid,
              owner: member.owner,
              role: member.accountRole,
            })
          })
        })
        setMembers(allMembers.filter((member) => member.id === accountId))
        setIsLoading(false)
      },
      (error) => {
        console.error('Error fetching members:', error)
        setIsLoading(false)
      }
    )

    return () => unsubscribe()
  }, [selectedSystem, currentUser, accountId])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const policyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(policyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date && data.id === members[0]?.owner?.id)
      setOwners(risksData)
    })

    return () => unsubscribe()
  }, [currentUser, members])

  const handleApprove = async (email: string, justificationNote?: string) => {
    if (!email) {
      showErrorToast({message: 'Owner is not assigned to this account'})
      return
    }
    if (email === currentUser?.email && !justificationNote) {
      setPendingAction('approve')
      setShowConfirmationModal(true)
      return
    }
    const requiredFields = {
      tenantId: currentUser?.tenantId || '',
      reviewId: id,
      systemId: systemId,
      accountId: accountId,
      memberName: members?.[0]?.name,
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key)

    if (missingFields.length > 0) {
      return
    }

    const {tenantId, reviewId, systemId: sysId, accountId: accId, memberName} = requiredFields

    try {
      const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)

      const reviewSnapshot = await getDoc(reviewRef)
      if (!reviewSnapshot.exists()) {
        throw new Error('Review document not found')
      }

      const reviewData = reviewSnapshot.data()
      if (!reviewData.systems) {
        throw new Error('No systems found in review document')
      }

      const userData: ReviewUserSystem = {
        userId: accId,
        name: memberName,
        status: 'Approved',
        reviewBy: {
          uid: currentUser?.uid || '',
          email: currentUser?.email || '',
          name: currentUser?.userName || '',
        },
        approvedAt: new Date().toISOString(),
        reasonForSelfReview:
          justificationNote || selectedSystem?.system?.users?.[accountId]?.note || '',
      }

      await updateDoc(reviewRef, {
        [`systems.${sysId}.users.${accId}`]: userData,
      })

      showSuccessToast({message: 'User approved successfully'})
      onClose()
    } catch (error) {
      let errorMessage = 'Error recording approval'
      if ((error as {code?: string}).code === 'permission-denied') {
        errorMessage = 'You do not have permission to approve this request'
      } else if ((error as {code?: string}).code === 'not-found') {
        errorMessage = 'Review document not found'
      } else if ((error as Error).message) {
        errorMessage = (error as Error).message
      }
      showErrorToast({message: errorMessage})
    }
  }

  const handleDeny = async (email: string, justificationNote?: string) => {
    if (!email) {
      showErrorToast({message: 'Owner is not assigned to this account'})
      return
    }
    if (email.trim() === currentUser?.email?.trim() && !justificationNote) {
      setPendingAction('deny')
      setShowConfirmationModal(true)
      return
    }
    const requiredFields = {
      tenantId: currentUser?.tenantId || '',
      reviewId: id,
      systemId: systemId,
      accountId: accountId,
      memberName: members?.[0]?.name,
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key)

    if (missingFields.length > 0) {
      return
    }

    const {tenantId, reviewId, systemId: sysId, accountId: accId, memberName} = requiredFields

    try {
      const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)

      const reviewSnapshot = await getDoc(reviewRef)
      if (!reviewSnapshot.exists()) {
        throw new Error('Review document not found')
      }

      const reviewData = reviewSnapshot.data()
      if (!reviewData.systems) {
        throw new Error('No systems found in review document')
      }

      const userData: ReviewUserSystem = {
        userId: accId,
        name: memberName,
        status: 'Denied',
        reviewBy: {
          uid: currentUser?.uid || '',
          email: currentUser?.email || '',
          name: currentUser?.userName || '',
        },
        approvedAt: new Date().toISOString(),
        reasonForSelfReview:
          justificationNote || selectedSystem?.system?.users?.[accountId]?.note || '',
      }

      await updateDoc(reviewRef, {
        [`systems.${sysId}.users.${accId}`]: userData,
      })

      showSuccessToast({message: 'User denied successfully'})
      onClose()
    } catch (error) {
      let errorMessage = 'Error recording denial'
      if ((error as {code?: string}).code === 'permission-denied') {
        errorMessage = 'You do not have permission to deny this request'
      } else if ((error as {code?: string}).code === 'not-found') {
        errorMessage = 'Review document not found'
      } else if ((error as Error).message) {
        errorMessage = (error as Error).message
      }
      showErrorToast({message: errorMessage})
    }
  }

  const handleSaveNote = async () => {
    setIsNoteSaving(true)
    const requiredFields = {
      tenantId: currentUser?.tenantId || '',
      reviewId: id,
      systemId: systemId,
      accountId: accountId,
      memberName: members?.[0]?.name,
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key)

    if (missingFields.length > 0) {
      showErrorToast({message: 'Missing required fields: ' + missingFields.join(', ')})
      return
    }

    const {tenantId, reviewId, systemId: sysId, accountId: accId, memberName} = requiredFields

    try {
      const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)

      const reviewSnapshot = await getDoc(reviewRef)
      if (!reviewSnapshot.exists()) {
        throw new Error('Review document not found')
      }

      const reviewData = reviewSnapshot.data()
      if (!reviewData.systems) {
        throw new Error('No systems found in review document')
      }

      const existingUserData: ReviewUserSystem = reviewData.systems[sysId]?.users?.[accId] || {
        userId: accId,
        name: memberName,
        status: '',
      }

      const userData: ReviewUserSystem = {
        ...existingUserData,
        note: noteInput.trim() || '',
      }

      await updateDoc(reviewRef, {
        [`systems.${sysId}.users.${accId}`]: userData,
      })
      setIsNoteSaving(false)
      showSuccessToast({message: 'Note saved successfully'})
    } catch (error) {
      let errorMessage = 'Error saving note'
      if ((error as {code?: string}).code === 'permission-denied') {
        errorMessage = 'You do not have permission to save this note'
      } else if ((error as {code?: string}).code === 'not-found') {
        errorMessage = 'Review document not found'
      } else if ((error as Error).message) {
        errorMessage = (error as Error).message
      }
      showErrorToast({message: errorMessage})
      setIsNoteSaving(false)
    }
  }

  const handleCloseDrawer = () => {
    setPendingAction(null)
    onClose()
  }

  const handleModalSubmit = (note: string, action: 'approve' | 'deny') => {
    if (action === 'approve') {
      handleApprove(members[0]?.owner?.email, note)
    } else if (action === 'deny') {
      handleDeny(members[0]?.owner?.email, note)
    }
  }

  const userStatus = useMemo(() => {
    return selectedSystem?.system?.users?.[accountId]?.status || ''
  }, [selectedSystem, accountId])

  if (loading) {
    return (
      <Drawer open={isOpen} onClose={handleCloseDrawer} direction='right' className='' size='45%'>
        <DrawerHeader title='Account Details' onClose={handleCloseDrawer} />
        <div
          className='p-6 d-flex justify-content-center align-items-center'
          style={{height: 'calc(100vh - 30px)'}}
        >
          <span className='spinner-border spinner-border-sm'></span>
          <span className='ms-2'>Loading...</span>
        </div>
      </Drawer>
    )
  }

  return (
    <>
      <Drawer open={isOpen} onClose={handleCloseDrawer} direction='right' className='' size='45%'>
        <DrawerHeader title={members?.[0]?.name || 'Account details'} onClose={handleCloseDrawer} />
        <div
          className=' scroll-y overflow-auto p-6 mb-4'
          tabIndex={-1}
          style={{height: 'calc(100vh - 40px)'}}
        >
          {members[0] && !members[0]?.owner?.name && (
            <div
              className='card'
              style={{
                backgroundColor: '#F3FAF7',
                border: '1.5px solid #0E9F6E',
                marginBottom: '24px',
              }}
            >
              <div className='px-6 py-4'>
                <div className='d-flex align-items-between justify-content-between'>
                  <div className='d-flex justify-content-start flex-column gap-1'>
                    <span className='card-label fw-semibold fs-4'>
                      No Owner Assigned to This Active Account
                    </span>
                    <span className='fw-normal fs-6' style={{color: '#5A5A5A'}}>
                      This active service account must be reviewed and assigned to an active owner
                      as part of your access review.
                    </span>
                  </div>

                  <div className='d-flex align-items-center gap-2'>
                    <Link to={`/access?system=${systemName}&userId=${members?.[0]?.id}`}>
                      <button
                        className='btn btn-sm d-flex fs-7 align-items-center fw-bold'
                        style={{
                          backgroundColor: '#0E9F6E',
                          color: '#FFFFFF',
                          height: '40px',
                        }}
                        onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                          (e.currentTarget.style.backgroundColor = '#148363')
                        }
                        onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                          (e.currentTarget.style.backgroundColor = '#0E9F6E')
                        }
                      >
                        Assign
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
          {members.length > 0 &&
            members.map((member, index) => (
              <div key={member.id}>
                <div className='row' style={{marginBottom: '20px'}}>
                  <div className='col-3'>
                    <label className='text-gray-800 fw-semibold me-2 fs-6'>Flagged</label>
                  </div>
                  <div className='col-9'>
                    <span className='fw-normal fs-6'>--</span>
                  </div>
                </div>
                <div className='row' style={{marginBottom: '20px'}}>
                  <div className='col-3 d-flex align-items-center'>
                    <label className='text-gray-800 fw-semibold me-2 fs-6'>Owner</label>
                  </div>
                  <div className='col-9'>
                    {member.owner ? <OwnerDisplay owner={member?.owner?.name} /> : '--'}
                  </div>
                </div>
                <div className='row' style={{marginBottom: '20px'}}>
                  <div className='col-3 d-flex align-items-center'>
                    <label className='text-gray-800 fw-semibold me-2 fs-6'>Role</label>
                  </div>
                  <div className='col-9'>
                    <span className='fw-normal fs-6'>{member.role || '--'}</span>
                  </div>
                </div>
                <div className='row' style={{marginBottom: '20px'}}>
                  <div className='col-3 d-flex align-items-center'>
                    <label className='text-gray-800 fw-semibold me-2 fs-6'>Secureslate Group</label>
                  </div>
                  <div className='col-9'>
                    {owners[0]?.group
                      ? owners[0].group.charAt(0).toUpperCase() + owners[0].group.slice(1)
                      : '--'}
                  </div>
                </div>
                <div className='separator my-10'></div>
                <div>
                  <div
                    className='d-flex align-items-center'
                    style={{
                      gap: '12px',
                      marginBottom: '12px',
                      padding: '0.5px',
                    }}
                  >
                    <div
                      className='flex-fill'
                      style={{
                        borderRadius: '8px',
                        padding: '0.5px',
                      }}
                    >
                      <div className='card'>
                        <div className='border-bottom' style={{padding: '16px 20px'}}>
                          <span className='fw-semibold fs-6 text-dark required me-2'>
                            Review access
                          </span>
                          {member?.owner?.email === currentUser?.email && (
                            <OverlayTrigger
                              placement='right'
                              overlay={
                                <Tooltip id='tooltip-top'>
                                  <span>
                                    As a security best practice, it is recommended that individuals
                                    do not review or approve their own access to ensure objectivity
                                    and maintain proper access control.
                                  </span>
                                </Tooltip>
                              }
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='#C10007'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-alert-circle'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                                <path d='M12 8v4' />
                                <path d='M12 16h.01' />
                              </svg>
                            </OverlayTrigger>
                          )}
                        </div>
                        <div className='card-body text-gray-900 fs-6' style={{padding: '20px'}}>
                          <div style={{marginBottom: '16px'}}>
                            Indicate whether this account should continue maintaining current levels
                          </div>
                          <div className='row d-flex align-items-center mb-5'>
                            <div className='col col-4'>Maintain access?</div>
                            <div
                              className='col col-8'
                              style={{
                                display: 'flex',
                                gap: '8px',
                              }}
                            >
                              <Button
                                btnType='primary'
                                onClick={() => handleApprove(member?.owner?.email)}
                                disabled={
                                  userStatus === 'Approved' ||
                                  userStatus === 'Denied' ||
                                  !members[0]?.owner?.name
                                }
                              >
                                Approve
                              </Button>
                              <Button
                                btnType='destructive'
                                onClick={() => handleDeny(member?.owner?.email)}
                                disabled={
                                  userStatus === 'Approved' ||
                                  userStatus === 'Denied' ||
                                  !members[0]?.owner?.name
                                }
                              >
                                Deny
                              </Button>
                            </div>
                          </div>
                          <div className='row mb-5'>
                            <div className='col col-4'>Status</div>
                            <div className='col col-8'>
                              {selectedSystem?.system?.users?.[accountId]
                                ? selectedSystem?.system?.users?.[accountId]?.status
                                : '--'}
                            </div>
                          </div>
                          <div className='row'>
                            <div className='col col-4'>Review by</div>
                            <div className='col col-8'>
                              {selectedSystem?.system?.users?.[accountId] ? (
                                <OwnerDisplay
                                  owner={selectedSystem?.system?.users?.[accountId]?.reviewBy?.name}
                                />
                              ) : (
                                '--'
                              )}
                            </div>
                          </div>
                          {selectedSystem?.system?.users?.[accountId] &&
                            selectedSystem.system.users[accountId].reasonForSelfReview && (
                              <div className='row mt-5'>
                                <div className='col col-4'>Reason for Self Review</div>
                                <div className='col col-8'>
                                  <textarea
                                    className='form-control mb-2 fs-7'
                                    placeholder='Add a note'
                                    name='notes'
                                    value={
                                      selectedSystem?.system?.users?.[accountId]
                                        ?.reasonForSelfReview || ''
                                    }
                                    rows={5}
                                    readOnly
                                  />
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      gap: '12px',
                      marginBottom: '20px',
                      padding: '0.5px',
                    }}
                  >
                    <div
                      className='flex-fill'
                      style={{
                        borderRadius: '8px',
                        padding: '0.5px',
                      }}
                    >
                      <div className='card'>
                        <div className='border-bottom' style={{padding: '16px 20px'}}>
                          <span className='fw-semibold fs-6 text-dark'>Add a note</span>
                        </div>
                        <div className='card-body text-gray-900 fs-6' style={{padding: '20px'}}>
                          <div style={{marginBottom: '16px'}}>
                            Add a note to explain decisions or to track remediation progress
                          </div>
                          <div>
                            <label
                              style={{fontSize: '14px', marginBottom: '8px', display: 'block'}}
                            >
                              Note
                            </label>
                            <textarea
                              className='form-control mb-2'
                              placeholder='Add a note'
                              name='notes'
                              value={noteInput}
                              onChange={(e) => setNoteInput(e.target.value)}
                              disabled={systemStatus}
                            />
                            <Button
                              btnType='primary'
                              onClick={handleSaveNote}
                              disabled={isNoteSaving || systemStatus}
                            >
                              {isNoteSaving ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </Drawer>
      <ReviewConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false)
          setPendingAction(null)
        }}
        id={id}
        onSubmit={handleModalSubmit}
        action={pendingAction || 'approve'}
      />
    </>
  )
}

export default ViewSystem
